import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { treatName, COMPANY_RE } from "@/components/WhatsAppIcon";

export const isCompany = (party) => !!party && COMPANY_RE.test(party.name || "");
const repParty = (party) => ({ name: party.representative, title: party.rep_title || { sr: "Sr.", sra: "Sra.", nome: "none" }[party.rep_treat] });
export const needsContact = (party, link) => isCompany(party) && !(party.representative && (link?.phone || link?.email || party.rep_phone));
export const remindName = (party, fallback) => (!party?.name ? fallback : isCompany(party) ? (party.representative ? party.rep_call || treatName(repParty(party)) : null) : treatName(party));
export const remindLabel = (party, fallback) => remindName(party, fallback);

const TREATS = [["sr", "Sr."], ["sra", "Sra."], ["nome", "Nome próprio"]];

export function CompanyContactDialog({ pid, role, idx = 0, party, onClose, onSaved }) {
  const [f, setF] = useState({ name: party?.representative || "", phone: (party?.rep_phone || "").replace(/^\+351\s?/, ""), email: party?.rep_email || "", treat: party?.rep_treat || "sr", statutes_require_minutes: !!party?.statutes_require_minutes });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/processes/${pid}/parties/${role === "vendedor" ? "sellers" : "buyers"}/${idx}/representative`, f);
      toast.success("Contacto da empresa guardado");
      onSaved?.(); onClose();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Não foi possível guardar"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="company-contact-dialog">
        <DialogHeader><DialogTitle className="font-heading text-ec-green">Contacto da empresa</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600 -mt-1">{party?.name}. Os lembretes e os pedidos de documentos (cartão de cidadão, certidão comercial) passam a ir para esta pessoa.</p>
        <div className="space-y-3">
          <div><Label>Nome do gerente</Label><Input value={f.name} onChange={set("name")} autoFocus data-testid="company-contact-name" /></div>
          <div><Label>Telemóvel</Label>
            <div className="flex items-center gap-2"><span className="h-9 px-3 inline-flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600">+351</span>
              <Input value={f.phone} onChange={set("phone")} inputMode="tel" placeholder="912 345 678" data-testid="company-contact-phone" /></div></div>
          <div><Label>Email <span className="text-slate-400 font-normal">(opcional)</span></Label><Input type="email" value={f.email} onChange={set("email")} data-testid="company-contact-email" /></div>
          <div><Label>Tratar por</Label>
            <div className="flex gap-2 mt-1">{TREATS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setF({ ...f, treat: k })} data-testid={`company-contact-treat-${k}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${f.treat === k ? "navy-bg text-white border-transparent" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l}</button>
            ))}</div></div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.statutes_require_minutes} onChange={(e) => setF({ ...f, statutes_require_minutes: e.target.checked })} data-testid="company-contact-statutes-minutes" />Os estatutos exigem ata para vender</label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600" data-testid="company-contact-cancel">Cancelar</button>
          <button onClick={save} disabled={busy || !f.name.trim() || !f.phone.trim()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold navy-bg text-white disabled:opacity-40" data-testid="company-contact-save">
            {busy && <Loader2 size={14} className="animate-spin" />}Guardar</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
